import { ChevronRightIcon, EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { type LanguageKey, resolveLanguageKey } from "@packages/core/language/language.ts";
import { useCurrentLanguage } from "@squide/i18next";
import { type ComponentProps, useLayoutEffect, useRef, useState } from "react";
import { Breadcrumb } from "react-aria-components/Breadcrumbs";
import { Breadcrumbs, BreadcrumbsItem } from "@/components/ui/breadcrumbs.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Menu, MenuContent, MenuItem, MenuLabel } from "@/components/ui/menu.tsx";
import { ReturnLink } from "./useReturnTo.tsx";
import { type BreadcrumbTier, type BreadcrumbTierCandidate, getApplicableTiers, selectBreadcrumbTier } from "./breadcrumbTiers.ts";

interface RoutedBreadcrumbItem {
    label: string;
    href?: string;
}

export interface RoutedBreadcrumbProps {
    ariaLabel: string;
    items: RoutedBreadcrumbItem[];
}

const COLLAPSED_CRUMBS_LABELS: Record<LanguageKey, string> = {
    "en-US": "Show more pages",
    "fr-CA": "Afficher plus de pages"
};

const CRUMB_LABEL_TRUNCATE = "[&>:first-child]:min-w-0 [&>:first-child]:truncate";

const ANCESTOR_CRUMB_CLASSES = `max-w-48 shrink-0 whitespace-nowrap ${CRUMB_LABEL_TRUNCATE}`;

const CURRENT_CRUMB_CLASSES = `min-w-16 whitespace-nowrap ${CRUMB_LABEL_TRUNCATE}`;

const MEASURED_ANCESTOR_CRUMB_CLASSES = "shrink-0 whitespace-nowrap";
const MEASURED_CURRENT_CRUMB_CLASSES = "whitespace-nowrap";

type TrailEntry = { kind: "crumb"; item: RoutedBreadcrumbItem } | { kind: "menu"; items: RoutedBreadcrumbItem[] };

function buildTrail(items: RoutedBreadcrumbItem[], tier: BreadcrumbTier): TrailEntry[] {
    if (tier === "full" || items.length < 2) {
        return items.map(item => ({ kind: "crumb", item }));
    }

    const current: TrailEntry = { kind: "crumb", item: items[items.length - 1] };

    if (tier === "collapseMiddle") {
        return [{ kind: "crumb", item: items[0] }, { kind: "menu", items: items.slice(1, -1) }, current];
    }

    return [{ kind: "menu", items: items.slice(0, -1) }, current];
}

interface BreadcrumbTrailProps {
    items: RoutedBreadcrumbItem[];
    tier: BreadcrumbTier;
    menuLabel: string;
    isMeasurementCopy: boolean;
}

function BreadcrumbTrail({ items, tier, menuLabel, isMeasurementCopy }: BreadcrumbTrailProps) {
    const ancestorClasses = isMeasurementCopy ? MEASURED_ANCESTOR_CRUMB_CLASSES : ANCESTOR_CRUMB_CLASSES;
    const currentClasses = isMeasurementCopy ? MEASURED_CURRENT_CRUMB_CLASSES : CURRENT_CRUMB_CLASSES;

    return (
        <Breadcrumbs>
            {buildTrail(items, tier).map(entry =>
                entry.kind === "menu" ? (
                    /* intent-ui-deviation: raw react-aria Breadcrumb + a copy of breadcrumbs.tsx's separator --
                       BreadcrumbsItem hardcodes a Link child, and this crumb is a menu trigger. */
                    <Breadcrumb key="collapsed-crumbs" className="flex shrink-0 items-center gap-2 text-sm" data-slot="breadcrumb-item">
                        <Menu>
                            <Button intent="plain" size="sq-sm" aria-label={isMeasurementCopy ? undefined : menuLabel} name={isMeasurementCopy ? undefined : "collapsed-crumbs"}>
                                <EllipsisHorizontalIcon aria-hidden="true" />
                            </Button>
                            <MenuContent placement="bottom start">
                                {entry.items.map(item => (
                                    <MenuItem
                                        key={item.href ?? item.label}
                                        href={item.href}
                                        isDisabled={!item.href}
                                        textValue={item.label}
                                        render={domProps => {
                                            if ("href" in domProps) {
                                                const { href, ...rest } = domProps;

                                                return <ReturnLink to={href} {...rest} />;
                                            }

                                            return <div {...domProps} />;
                                        }}
                                    >
                                        <MenuLabel>{item.label}</MenuLabel>
                                    </MenuItem>
                                ))}
                            </MenuContent>
                        </Menu>
                        <span className="*:text-muted-fg *:shrink-0 *:data-[slot=icon]:size-3.5">
                            <ChevronRightIcon />
                        </span>
                    </Breadcrumb>
                ) : entry.item.href ? (
                    <BreadcrumbsItem
                        key={entry.item.href}
                        href={entry.item.href}
                        className={`text-muted-fg ${ancestorClasses} [&_a:hover]:text-fg [&_a]:underline-offset-4 [&_a:hover]:underline`}
                        render={domProps => {
                            if ("href" in domProps) {
                                const { href, ...rest } = domProps;

                                return <ReturnLink to={href} {...rest} />;
                            }

                            return <li {...(domProps as ComponentProps<"li">)} />;
                        }}
                    >
                        {entry.item.label}
                    </BreadcrumbsItem>
                ) : (
                    <BreadcrumbsItem key={entry.item.label} className={`text-fg font-medium ${currentClasses}`}>
                        {entry.item.label}
                    </BreadcrumbsItem>
                )
            )}
        </Breadcrumbs>
    );
}

export function RoutedBreadcrumb({ ariaLabel, items }: RoutedBreadcrumbProps) {
    const menuLabel = COLLAPSED_CRUMBS_LABELS[resolveLanguageKey(useCurrentLanguage())];

    const navRef = useRef<HTMLElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [tier, setTier] = useState<BreadcrumbTier>("full");

    useLayoutEffect(() => {
        const navEl = navRef.current;
        const measureEl = measureRef.current;

        if (!navEl || !measureEl) {
            return;
        }

        const fit = () => {
            const tiers = getApplicableTiers(items.length);
            const candidates: BreadcrumbTierCandidate[] = [...measureEl.children].map((child, index) => ({
                tier: tiers[index],
                width: child.getBoundingClientRect().width
            }));

            setTier(selectBreadcrumbTier(candidates, navEl.clientWidth));
        };

        fit();

        const observer = new ResizeObserver(fit);
        observer.observe(navEl);
        observer.observe(measureEl);

        return () => observer.disconnect();
    }, [items.length]);

    return (
        <nav ref={navRef} aria-label={ariaLabel} className="w-full min-w-0">
            <div className="-m-1.5 overflow-hidden p-1.5">
                <div className="relative h-0 w-0 overflow-hidden">
                    <div ref={measureRef} aria-hidden="true" inert className="pointer-events-none absolute top-0 left-0 flex">
                        {getApplicableTiers(items.length).map(candidate => (
                            <div key={candidate} className="w-max">
                                <BreadcrumbTrail items={items} tier={candidate} menuLabel={menuLabel} isMeasurementCopy />
                            </div>
                        ))}
                    </div>
                </div>

                <BreadcrumbTrail items={items} tier={tier} menuLabel={menuLabel} isMeasurementCopy={false} />
            </div>
        </nav>
    );
}
